import React, { useEffect, useState, useCallback } from "react";
import {
  TableBody,
  TableHead,
  TableRow,
  Typography,
  useTheme,
  useMediaQuery,
} from "@mui/material";
import { East } from "@mui/icons-material";
import dayjs from "dayjs";
import CustomModal from "../../Common/CustomModal";
import CustomDatePicker from "../../Common/CustomDatePicker";
import axiosInstance from "../../../utils/config/config";
import {
  LESSONS_COUNT,
  TABLE_HEADERS,
  SAVE_BTN_TEXT,
  CANCEL_BTN_TEXT,
  API_ENDPOINTS,
  ERROR_MESSAGES,
} from "./MonasteryProgressModal.constants";
import {
  ContentBox,
  ScrollableTableContainer,
  ProgressTable,
  HeaderCell,
  BodyCell,
  LessonNumberCell,
  LessonCardPaper,
  LessonCardGrid,
  MobileListBox,
} from "./MonasteryProgressModal.styles";
import {
  buildInitialLessons,
  generateProgressPdf,
} from "./MonasteryProgressModal.utils";

const hasLessonData = ({ startDate, profiencyDate }) =>
  Boolean(startDate || profiencyDate);

const TableView = ({ lessons, handleDateChange }) => (
  <ScrollableTableContainer>
    <ProgressTable size="small">
      <TableHead>
        <TableRow>
          <HeaderCell>{TABLE_HEADERS.lessonNumber}</HeaderCell>
          <HeaderCell>{TABLE_HEADERS.startingDate}</HeaderCell>
          <HeaderCell>{TABLE_HEADERS.dateOfProficiency}</HeaderCell>
        </TableRow>
      </TableHead>
      <TableBody>
        {lessons.map((lesson) => (
          <TableRow key={lesson.lessonNumber}>
            <LessonNumberCell>{lesson.lessonNumber}</LessonNumberCell>
            <BodyCell>
              <CustomDatePicker
                value={lesson.startDate}
                setValue={(date) =>
                  handleDateChange(lesson.lessonNumber, "startDate", date)
                }
              />
            </BodyCell>
            <BodyCell>
              <CustomDatePicker
                value={lesson.profiencyDate}
                setValue={(date) =>
                  handleDateChange(lesson.lessonNumber, "profiencyDate", date)
                }
              />
            </BodyCell>
          </TableRow>
        ))}
      </TableBody>
    </ProgressTable>
  </ScrollableTableContainer>
);

const LessonCard = ({ lesson, onDateChange }) => (
  <LessonCardPaper variant="outlined">
    <LessonCardGrid>
      <Typography variant="caption" color="text.secondary">
        {TABLE_HEADERS.lessonNumber}
      </Typography>
      <Typography variant="body2" fontWeight={700}>
        {lesson.lessonNumber}
      </Typography>

      <Typography variant="caption" color="text.secondary">
        {TABLE_HEADERS.startingDate}
      </Typography>
      <CustomDatePicker
        value={lesson.startDate}
        setValue={(date) =>
          onDateChange(lesson.lessonNumber, "startDate", date)
        }
      />

      <Typography variant="caption" color="text.secondary">
        {TABLE_HEADERS.dateOfProficiency}
      </Typography>
      <CustomDatePicker
        value={lesson.profiencyDate}
        setValue={(date) =>
          onDateChange(lesson.lessonNumber, "profiencyDate", date)
        }
      />
    </LessonCardGrid>
  </LessonCardPaper>
);

const MonasteryProgressModal = ({
  open,
  handleClose,
  setLoading,
  setSuccess,
  setErrorState,
}) => {
  const [lessons, setLessons] = useState(() =>
    buildInitialLessons(LESSONS_COUNT)
  );

  const fetchProgress = useCallback(async () => {
    try {
      const response = await axiosInstance.get(API_ENDPOINTS.get);
      if (response.status !== 200) return;

      const fetched = response.data.monasteryProgress || [];
      setLessons((prev) =>
        prev.map((lesson) => {
          const match = fetched.find(
            (item) => item.lessonNumber === lesson.lessonNumber
          );
          return match
            ? {
                ...lesson,
                startDate: dayjs(match.startDate) || null,
                profiencyDate: dayjs(match.profiencyDate) || null,
              }
            : lesson;
        })
      );
    } catch (error) {
      console.error("Error fetching monastery progress", error);
    }
  }, []);

  useEffect(() => {
    if (open) fetchProgress();
  }, [open, fetchProgress]);

  const handleDateChange = (lessonNumber, field, value) => {
    setLessons((prev) =>
      prev.map((lesson) =>
        lesson.lessonNumber === lessonNumber
          ? { ...lesson, [field]: value ?? lesson[field] }
          : lesson
      )
    );
  };

  const handleSave = async () => {
    setErrorState({ error: false, message: "" });
    const validLessons = lessons.filter(hasLessonData);

    try {
      setLoading(true);
      const response = await axiosInstance.post(API_ENDPOINTS.upsert, {
        lessons: validLessons,
      });

      if (response.status === 200) {
        setSuccess(true);
        setErrorState({ error: false, message: "" });
        handleClose();
        fetchProgress();
      }
    } catch (error) {
      console.error("Error saving monastery progress:", error);
      setErrorState({ error: true, message: ERROR_MESSAGES.save });
    } finally {
      setLoading(false);
    }
  };

  const handleDownloadPDF = () => generateProgressPdf(lessons);

  const hasAnyData = lessons.some(hasLessonData);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));

  return (
    <CustomModal
      open={open}
      handleClose={handleClose}
      title={"MODAL_TITLE"}
      saveBtnText={SAVE_BTN_TEXT}
      cancelBtnText={CANCEL_BTN_TEXT}
      disableBtn={!hasAnyData}
      saveIcon={East}
      onSave={handleSave}
      onCancel={handleDownloadPDF}
    >
      <ContentBox>
        {isMobile ? (
          <MobileListBox>
            {lessons.map((lesson) => (
              <LessonCard
                key={lesson.lessonNumber}
                lesson={lesson}
                onDateChange={handleDateChange}
              />
            ))}
          </MobileListBox>
        ) : (
          <TableView lessons={lessons} handleDateChange={handleDateChange} />
        )}
      </ContentBox>
    </CustomModal>
  );
};

export default MonasteryProgressModal;
